import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ShieldAlert, Tags, Users, LayoutGrid } from "lucide-react";
import { AdminOverview } from "@/pages/AdminOverview";
import { api, errMsg, mediaUrl } from "@/lib/api";
import { hubByKey } from "@/lib/hubs";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";

const Act = ({ id, action, label, onDone, danger }) => (
  <button data-testid={`admin-action-${action}-${id}`} onClick={async () => {
    try { await api.post(`/admin/tracks/${id}/action`, { action }); toast.success(`${label} ✓`); onDone(); } catch (e) { toast.error(errMsg(e)); }
  }} className={`px-3 h-8 rounded-full text-xs font-semibold border ${danger ? "border-red-500/50 text-red-300 hover:bg-red-500/10" : "border-white/15 hover:border-white/40"}`}>{label}</button>
);

export default function Admin() {
  const [queue, setQueue] = useState([]);
  const [flagged, setFlagged] = useState([]);
  const [artists, setArtists] = useState([]);
  const load = () => {
    api.get("/admin/queue").then((r) => setQueue(r.data));
    api.get("/admin/flagged").then((r) => setFlagged(r.data));
    api.get("/admin/artists").then((r) => setArtists(r.data));
  };
  useEffect(load, []);
  return (
    <div data-testid="admin-page" className="reveal">
      <div className="eyebrow">Trust & Safety</div>
      <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tight mt-2">Admin Dashboard</h1>
      <Tabs defaultValue="overview" className="mt-8">
        <TabsList className="bg-white/5 flex-wrap h-auto">
          <TabsTrigger value="overview" data-testid="admin-tab-overview"><LayoutGrid size={14} className="mr-2" />Users & plans</TabsTrigger>
          <TabsTrigger value="queue" data-testid="admin-tab-queue"><ShieldAlert size={14} className="mr-2" />Priority queue ({queue.length})</TabsTrigger>
          <TabsTrigger value="flags" data-testid="admin-tab-flags"><Tags size={14} className="mr-2" />Tag flags ({flagged.length})</TabsTrigger>
          <TabsTrigger value="artists" data-testid="admin-tab-artists"><Users size={14} className="mr-2" />Whitelist</TabsTrigger>
        </TabsList>
        <TabsContent value="overview"><AdminOverview /></TabsContent>
        <TabsContent value="queue">
          <div data-testid="admin-moderation-queue-table" className="surface divide-y divide-white/5 mt-4">
            {queue.length === 0 && <div className="p-6 text-zinc-500">Queue is clear.</div>}
            {queue.map((i) => (
              <div key={i.item_id} className="p-5 flex flex-col lg:flex-row gap-4 lg:items-center">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2"><span className={`text-[10px] font-mono uppercase px-2 py-0.5 rounded ${i.type === "dmca" ? "bg-red-500/20 text-red-300" : "bg-amber-500/20 text-amber-300"}`}>{i.type === "dmca" ? "DMCA · priority" : "Human review request"}</span>
                    {i.deadline && <span className="text-xs font-mono text-zinc-500">Act before {new Date(i.deadline).toLocaleString()}</span>}</div>
                  <Link to={`/track/${i.track_id}`} className="font-semibold mt-1 block hover:underline">{i.track?.title || i.track_id}</Link>
                  <p className="text-sm text-zinc-400 mt-1">{i.type === "dmca" ? `${i.claimant_name} <${i.claimant_email}>: ${i.work_description}` : i.message || "Artist contests automated tag flag."}</p>
                  <div className="text-xs font-mono text-zinc-600 mt-1">Track status: {i.track?.status}</div>
                </div>
                <div className="flex flex-wrap gap-2">
                  {i.type === "dmca" ? (<><Act id={i.track_id} action="suspend" label="Suspend track" danger onDone={load} /><Act id={i.track_id} action="dismiss" label="Dismiss" onDone={load} /></>)
                    : (<><Act id={i.track_id} action="clear_flag" label="Clear flag" onDone={load} /><Act id={i.track_id} action="remove_from_hub" label="Remove from hub" onDone={load} /><Act id={i.track_id} action="dismiss" label="Keep flag" onDone={load} /></>)}
                </div>
              </div>
            ))}
          </div>
        </TabsContent>
        <TabsContent value="flags">
          <div className="surface divide-y divide-white/5 mt-4" data-testid="admin-flagged-list">
            {flagged.map((t) => (
              <div key={t.track_id} className="p-5 flex flex-col lg:flex-row gap-4 lg:items-center">
                <div className="flex-1">
                  <div className="font-semibold">{t.title} <span className="text-xs font-mono text-zinc-500 ml-2">{t.genres.map((g) => hubByKey(g)?.name).join(" · ")}</span></div>
                  <p className="text-sm text-amber-300/90 mt-1">{t.tag_flag.issues?.join("; ")}</p>
                  <div className="text-xs font-mono text-zinc-600 mt-1">{t.tag_flag.status} · {t.tag_flag.engine} · status {t.status}</div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Act id={t.track_id} action="clear_flag" label="Clear flag" onDone={load} />
                  {t.status === "suspended" ? <Act id={t.track_id} action="reinstate" label="Reinstate" onDone={load} /> : <Act id={t.track_id} action="suspend" label="Suspend" danger onDone={load} />}
                </div>
              </div>
            ))}
          </div>
        </TabsContent>
        <TabsContent value="artists">
          <p className="text-sm text-zinc-400 mt-4">Manually verified profiles bypass automated vocal/genre checks permanently.</p>
          <div className="surface divide-y divide-white/5 mt-4">
            {artists.map((a) => (
              <div key={a.user_id} className="p-4 flex items-center gap-3">
                <img src={mediaUrl(a.avatar)} alt="" className="w-9 h-9 rounded-full object-cover bg-zinc-800" />
                <div className="flex-1"><div className="font-semibold">{a.name}</div><div className="text-xs font-mono text-zinc-500">{a.plan} · {a.track_count} tracks</div></div>
                <Switch data-testid={`admin-verify-${a.slug}`} checked={!!a.verified} onCheckedChange={async (v) => { await api.post(`/admin/artists/${a.user_id}/verify`, { verified: v }); load(); }} />
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
